import { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import type { Booking } from '@/types'
import {
    Calendar,
    Clock,
    CheckCircle,
    Clock4,
    XCircle,
    Search,
    User as UserIcon,
    Filter,
    LayoutList,
    ShieldCheck,
    CreditCard,
    PieChart,
    Send
} from 'lucide-react'
import LoadingState from '@/components/ui/LoadingState'
import EmptyState from '@/components/ui/EmptyState'
import { Link } from 'react-router-dom'
import ApprovalQueuePage from './ApprovalQueuePage'
import PaymentQueuePage from './PaymentQueuePage'
import ReportsPage from './ReportsPage'
import SendNotificationModal from '@/components/Admin/SendNotificationModal'

export default function AdminBookingsPage() {
    const { currentOrg } = useAuthStore()
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<string>('ALL')
    const [searchTerm, setSearchTerm] = useState('')
    const [activeTab, setActiveTab] = useState<'all' | 'approvals' | 'payments' | 'reports'>('all')
    const [notificationModal, setNotificationModal] = useState<{ userId: string, userName: string, userPhone?: string } | null>(null)
    const [limitCount, setLimitCount] = useState(10)

    useEffect(() => {
        if (!currentOrg) return

        const q = query(
            collection(db, 'bookings'),
            where('orgId', '==', currentOrg.id),
            orderBy('createdAt', 'desc'),
            limit(limitCount + 1) // Fetch one extra to check if there are more
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedBookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking))
            setBookings(loadedBookings) // Store all (slice later if needed, or just show all)
            // Actually, if we fetch 11, we should show 10? No, standard is show all loaded.
            // But user wants "Show 10, then expand".
            // If I fetch 11, and I show 11, next time I fetch 21...
            // The issue is "Expandir o restante".
            // Let's keep showing all loaded, but fetch allows us to know if 'limit' was reached.
            setLoading(false)
        }, (error) => {
            console.error('Erro ao carregar reservas:', error)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [currentOrg?.id, limitCount])

    const getStatusConfig = (status: Booking['status']) => {
        switch (status) {
            case 'CONFIRMED': return { label: 'Confirmado', icon: CheckCircle, className: 'bg-green-100 text-green-700 border-green-200' }
            case 'PENDING_APPROVAL': return { label: 'Aprovação', icon: Clock4, className: 'bg-[#001c28]/10 text-[#001c28] border-[#001c28]/20' }
            case 'PENDING_PAYMENT': return { label: 'Pagamento', icon: CreditCard, className: 'bg-[#d4a94d]/10 text-[#d4a94d] border-[#d4a94d]/20' }
            case 'CANCELED': return { label: 'Cancelado', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' }
            default: return { label: status, icon: Clock, className: 'bg-gray-100 text-gray-700 border-gray-200' }
        }
    }

    const filteredBookings = bookings.filter(b => {
        const matchesStatus = statusFilter === 'ALL' || b.status === statusFilter
        const matchesSearch =
            (b as any).spaceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
        return matchesStatus && matchesSearch
    })

    return (
        <div className="max-w-6xl w-full mx-auto p-6 space-y-8 page-enter">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b-2 border-[#001c28]/5">
                <div className="space-y-2">
                    <h1 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-[0.9]">Gestão de Reservas</h1>
                    <p className="font-bold text-[#001c28]/80 text-sm max-w-2xl">
                        Visualize e gerencie todas as reservas da organização.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 bg-white p-1.5 rounded-xl border border-[#001c28]/10 shadow-sm">
                    {[
                        { id: 'all', label: 'Todas', icon: LayoutList },
                        { id: 'approvals', label: 'Aprovações', icon: ShieldCheck },
                        { id: 'payments', label: 'Pagamentos', icon: CreditCard },
                        { id: 'reports', label: 'Relatórios', icon: PieChart }
                    ].map((tab) => {
                        const Icon = tab.icon
                        const isActive = activeTab === tab.id
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black transition-all uppercase tracking-wider ${isActive
                                    ? 'bg-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                                    : 'text-[#001c28]/40 hover:text-[#001c28] hover:bg-[#001c28]/5'
                                    }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {tab.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            {activeTab === 'all' && (
                <div className="animate-in fade-in slide-in-from-left-4 duration-500 space-y-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="relative flex-1 w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/30" />
                            <input
                                type="text"
                                placeholder="Buscar por nome do espaço ou usuário..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white border border-[#001c28]/10 rounded-xl pl-10 pr-4 py-3 text-sm font-bold text-[#001c28] focus:border-[#d4a94d] outline-none transition-all placeholder:text-[#001c28]/30 shadow-sm"
                            />
                        </div>
                        {/* Filtro Status */}
                        <div className="relative w-full md:w-auto">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/40" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full md:w-auto pl-10 pr-8 py-3 bg-white border border-[#001c28]/10 rounded-xl text-xs font-black uppercase tracking-wider text-[#001c28] focus:border-[#d4a94d] outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                <option value="ALL">Todos os Status</option>
                                <option value="PENDING_APPROVAL">Aprovação</option>
                                <option value="PENDING_PAYMENT">Pagamento</option>
                                <option value="CONFIRMED">Confirmado</option>
                                <option value="CANCELED">Cancelado</option>
                            </select>
                        </div>

                        <button
                            onClick={() => setNotificationModal({ userId: 'ALL', userName: 'Todos os Usuários' })}
                            className="w-full md:w-auto px-6 py-3 bg-[#001c28] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-[#001c28]/20 hover:bg-[#001c28]/90 transition-all active:scale-95 flex items-center justify-center gap-2"
                        >
                            <Send className="w-3.5 h-3.5" />
                            Enviar em Massa
                        </button>
                    </div>

                    {loading ? (
                        <LoadingState />
                    ) : filteredBookings.length === 0 ? (
                        <EmptyState
                            icon={Calendar}
                            title="Nenhuma reserva encontrada"
                            description={searchTerm || statusFilter !== 'ALL' ? "Nenhum resultado para os filtros aplicados." : "Ainda não há reservas registradas na organização."}
                        />
                    ) : (
                        <div className="bg-white rounded-[2rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5 overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-[#001c28]/[0.02] border-b border-[#001c28]/5">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Data</th>
                                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Espaço</th>
                                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Usuário</th>
                                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Status</th>
                                        <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Valor</th>
                                        <th className="px-6 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#001c28]/5">
                                    {filteredBookings.map((booking) => {
                                        const status = getStatusConfig(booking.status)
                                        const StatusIcon = status.icon

                                        return (
                                            <tr key={booking.id} className="group hover:bg-[#001c28]/[0.02] transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-[#001c28] text-sm">
                                                            {booking.start?.toDate().toLocaleDateString('pt-BR')}
                                                        </span>
                                                        <span className="text-xs text-[#001c28]/50 font-medium">
                                                            {booking.start?.toDate().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="font-bold text-[#001c28]/80 text-sm">{(booking as any).spaceName || 'N/A'}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2 group/user relative">
                                                        <div className="w-6 h-6 rounded-full bg-[#001c28]/10 flex items-center justify-center text-[#001c28]">
                                                            <UserIcon className="w-3 h-3" />
                                                        </div>
                                                        <span className="text-sm font-medium text-[#001c28]/80 cursor-help border-b border-dashed border-[#001c28]/20">{booking.userName || 'Usuário sem nome'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${status.className}`}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {status.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className="font-bold text-[#001c28]">R$ {booking.totalPrice?.toFixed(2)}</span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-3">
                                                        <Link to={`/bookings/${booking.id}`} className="text-[#001c28] font-bold text-[10px] uppercase tracking-wider hover:text-[#d4a94d] transition-colors">
                                                            Detalhes
                                                        </Link>
                                                        <button
                                                            onClick={() => setNotificationModal({
                                                                userId: booking.userId,
                                                                userName: booking.userName || 'Usuário',
                                                                userPhone: booking.userPhone
                                                            })}
                                                            className="text-[#001c28]/30 hover:text-[#001c28] transition-colors"
                                                            title="Enviar Mensagem"
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Load More Button */}
                    {!loading && bookings.length >= limitCount && (
                        <div className="flex justify-center pt-4">
                            <button
                                onClick={() => setLimitCount(prev => prev + 10)}
                                className="px-6 py-3 bg-white border border-[#001c28]/10 text-[#001c28] font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-[#001c28]/5 transition-colors shadow-sm"
                            >
                                Ver Mais
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'approvals' && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                    <ApprovalQueuePage embedded />
                </div>
            )}

            {activeTab === 'payments' && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                    <PaymentQueuePage embedded />
                </div>
            )}

            {activeTab === 'reports' && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                    <ReportsPage embedded />
                </div>
            )}

            {/* Notification Modal */}
            <SendNotificationModal
                isOpen={!!notificationModal}
                onClose={() => setNotificationModal(null)}
                userId={notificationModal?.userId}
                userName={notificationModal?.userName}
                userPhone={notificationModal?.userPhone}
            />
        </div>
    )
}
